import Fuse from 'fuse.js';
import { z } from 'zod';

import { opMeta } from '../op-meta.ts';
import { booleanCol } from '../schema/types.ts';
import type { Value } from '../value.ts';
import { compileHoistedBinary, defineBinary, prefixStringify } from './helpers.ts';

// String prefix test. Both arguments coerce to string at parse time
// (eager, via the `prefixStringify` schema transform) and at compile
// time (per-row coercion via `defineBinary`'s `coerce` callback) — the
// two paths share `prefixStringify` so they stay in sync. Match is
// case-insensitive (LLM-facing tool — casing must be forgiving). CDF
// push-down maps `prefix` to the `prefix` filter leaf, which
// matches case-SENSITIVELY server-side — this divergence is accepted
// by design (see `dateAwareEquals`).
export const prefix = z
  .object({
    name: z.literal('prefix'),
    params: z.tuple([z.unknown().transform(prefixStringify), z.unknown().transform(prefixStringify)]),
  })
  .describe('String prefix test. (prefix str pat) is true iff str starts with pat (case-insensitive).')
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(prefix "hello" "he")', result: 'true' },
    ...defineBinary<string>((str, pat) => str.toLowerCase().startsWith(pat.toLowerCase()), prefixStringify, booleanCol),
  });

/**
 * Regex match. `(re-match str pattern)` is true iff `pattern` (a JS
 * regex source string) matches anywhere in `str`. Case-insensitive by
 * default — embed `^`/`$` anchors for whole-string match or `(?-i)` /
 * an alternative pattern for case-sensitive. The RegExp is compiled
 * once per query when the pattern is a compile-time literal (the
 * common case for filter predicates); for dynamic patterns
 * (per-row column references) compilation is memoised on the resolved
 * pattern string so a constant-per-row pattern still pays at most one
 * `new RegExp` per query. Push-down: not supported — runs client-side
 * post-fetch.
 */
export const reMatch = z
  .object({
    name: z.literal('re-match'),
    params: z.tuple([z.unknown().transform(prefixStringify), z.unknown().transform(prefixStringify)]),
  })
  .describe(
    'Regex match. (re-match str pattern) is true iff `pattern` (JS regex source) matches anywhere in `str`. Case-insensitive.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    terseGroup: 'compare',
    example: { expr: '(re-match "Active Well" "^active")', result: 'true' },
    apply: ({ params }: { readonly params: readonly unknown[] }): Value =>
      new RegExp(String(params[1]), 'i').test(String(params[0])),
    expr: {
      compileExpr: (call, compile, path) =>
        compileHoistedBinary(
          call,
          compile,
          path,
          (pat) => new RegExp(pat, 'i'),
          (re, str) => re.test(str),
        ),
      inferType: () => booleanCol,
    },
  });

/**
 * Reusable per-pattern matcher built by `buildFuzzySearcher` from
 * fuse.js's `_getSearcher`. Underscore-prefixed in fuse but the
 * `Searcher` interface and `_getSearcher` signature are both
 * declared in fuse's published .d.ts; treating it as a typed
 * public-via-convention API. Pinned to fuse 7.3.0 in the workspace
 * `package.json`; the regression test `fuzzy-searcher.test.ts`
 * verifies the API surface holds across reinstalls.
 */
interface FuzzySearcher {
  readonly searchIn: (text: string) => { readonly isMatch: boolean; readonly score: number };
}

/**
 * Module-scope Fuse cache keyed by the options that determine the instance
 * (threshold + ignoreLocation). The Fuse instance is constructed once per
 * distinct option set and reused for every subsequent
 * {@link buildFuzzySearcher} call with those options — its empty haystack
 * means there's no per-instance state beyond the options, so sharing is safe.
 * `isCaseSensitive` is fixed `false` across the project's fuzzy SSOT
 * (closestMatches and `(fuzzy …)` both lowercase-compare), so it doesn't
 * enter the cache key.
 *
 * Without this, `compileHoistedBinary`'s dynamic-pattern branch built
 * a fresh `new Fuse([], …)` on every cache miss — for a per-row
 * varying pattern across 50k rows that's 50k Fuse constructions,
 * dominating the budget the matcher hoisting was supposed to save.
 */
const fuseByOptions = new Map<string, Readonly<{ _getSearcher: (q: string) => FuzzySearcher }>>();

/**
 * Build a reusable {@link FuzzySearcher} for a given pattern + threshold.
 * The returned searcher is allocated ONCE per call and `.searchIn(text)`
 * is fast (Bitap O(n·m/w)). The underlying Fuse instance is memoised
 * by its options via {@link fuseByOptions} so a per-row varying pattern
 * doesn't re-allocate Fuse; only the lightweight per-pattern Searcher
 * is constructed each call. Uses fuse's `_getSearcher` to skip the
 * haystack-indexing step entirely (the haystack is empty; we only
 * want the per-pattern matcher).
 *
 * `ignoreLocation` (default `false`, matching Fuse's own default and so
 * leaving existing callers' label-oriented scoring unchanged) drops the
 * positional penalty so a short pattern matches approximately ANYWHERE in
 * a long text — needed when searching free-text fields (e.g. a paragraph-
 * length comment) rather than short labels.
 */
export const buildFuzzySearcher = (
  pattern: string,
  threshold: number,
  options?: { readonly ignoreLocation?: boolean },
): FuzzySearcher => {
  const ignoreLocation = options?.ignoreLocation ?? false;
  const cacheKey = `${threshold}:${ignoreLocation ? 1 : 0}`;
  let fuse = fuseByOptions.get(cacheKey);
  if (fuse === undefined) {
    fuse = new Fuse<string>([], { threshold, isCaseSensitive: false, ignoreLocation }) as unknown as {
      readonly _getSearcher: (q: string) => FuzzySearcher;
    };
    fuseByOptions.set(cacheKey, fuse);
  }
  return fuse._getSearcher(pattern);
};

/**
 * Fuzzy match. `(fuzzy str pattern :threshold 0.4)` is true iff
 * `pattern` fuzzy-matches `str` (Bitap distance ≤ `threshold`; 0 =
 * exact, 1 = anything goes; default 0.4). Case-insensitive. Powered by
 * fuse.js — the project-wide fuzzy SSOT shared with `closestMatches`
 * (error "did you mean?" suggestions, recovery `closeMatches`) and the
 * data-model spotlight. Use for typo-tolerant filtering on user-facing
 * strings (well names, status enums, …). The Bitap searcher is built
 * ONCE per query when the pattern is a compile-time literal — never
 * per row — via fuse's `_getSearcher`. Push-down: not supported — runs
 * client-side post-fetch.
 */
export const fuzzy = z
  .object({
    name: z.literal('fuzzy'),
    params: z.tuple([z.unknown().transform(prefixStringify), z.unknown().transform(prefixStringify)]),
    threshold: z.number().min(0).max(1).optional(),
  })
  .describe(
    'Fuzzy match. (fuzzy str pattern :threshold 0.4) is true iff pattern is close to str (Bitap; 0 = exact, 1 = anything; default 0.4). Case-insensitive.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    terseGroup: 'compare',
    example: { expr: '(fuzzy "Active Well" "actve")', result: 'true' },
    apply: ({ params, threshold }: { readonly params: readonly unknown[]; threshold?: number }): Value =>
      buildFuzzySearcher(String(params[1]), threshold ?? 0.4).searchIn(String(params[0])).isMatch,
    expr: {
      compileExpr: (call, compile, path) => {
        const threshold = (call.threshold as number | undefined) ?? 0.4;
        return compileHoistedBinary(
          call,
          compile,
          path,
          (pat) => buildFuzzySearcher(pat, threshold),
          (searcher, str) => searcher.searchIn(str).isMatch,
        );
      },
      inferType: () => booleanCol,
    },
  });
